import { useEffect, useState, useCallback } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api, errMsg } from "@/lib/api";
import { useDash } from "./ctx";
import { toast } from "sonner";
import { ShieldCheck, Plus, Trash2, Users2, Eye, Pencil, Ban, Sparkles, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { V } from "@/lib/vocab";
import { InfoTip } from "@/components/InfoTip";

const LEVEL_META = {
  none: { label: "No access", icon: Ban, cls: "text-ink/40" },
  view: { label: "View", icon: Eye, cls: "text-sky-700" },
  edit: { label: "Can edit", icon: Pencil, cls: "text-emerald-700" },
};
const LEVELS = ["none", "view", "edit"];

// Areas where "Can edit" would be a dead grant: settings follow the community's Redline dials and team
// invites need a Community Manager / Administrator seat — a custom role can only ever browse them.
const viewOnlyAreas = () => ({ settings: `Custom roles can browse Settings & Team but never change them — settings follow the ${V.lc("org")}'s Redline dials and invites need ${V.a("staff")} or ${V.role("company_admin")} seat.` });

function LevelPicker({ value, onChange, testid, viewCap }) {
  const levels = viewCap ? ["none", "view"] : LEVELS;
  return (
    <div className="inline-flex shrink-0 rounded-full border border-sand-200 bg-white p-0.5" data-testid={testid} title={viewCap || undefined}>
      {levels.map((lv) => {
        const M = LEVEL_META[lv];
        const active = (value || "none") === lv;
        return (
          <button key={lv} type="button" data-testid={`${testid}-${lv}`} onClick={() => onChange(lv)}
            className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${active ? (lv === "edit" ? "bg-emerald-600 text-white" : lv === "view" ? "bg-sky-600 text-white" : "bg-ink text-white") : "text-ink/50 hover:text-ink"}`}>
            <M.icon className="h-3 w-3" strokeWidth={2} /> {M.label}
          </button>
        );
      })}
    </div>
  );
}

function RoleEditor({ open, onClose, role, catalog, activeAreas, templates, companyId, onSaved }) {
  const VIEW_ONLY_AREAS = viewOnlyAreas();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [perms, setPerms] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setName(role?.name || "");
    setDescription(role?.description || "");
    setPerms(role?.permissions ? { ...role.permissions } : {});
  }, [role, open]);

  const applyTemplate = (key) => {
    const t = templates?.[key];
    if (!t) return;
    if (!name.trim()) setName(t.name || "");
    if (!description.trim()) setDescription(t.description || "");
    setPerms({ ...(t.permissions || {}) });
    toast.success(`Loaded the ${t.name} template — tweak it below`);
  };

  const setArea = (k, lv) => setPerms((p) => { const n = { ...p }; if (lv === "none") delete n[k]; else n[k] = lv; return n; });
  const grantedCount = Object.values(perms).filter((v) => v === "view" || v === "edit").length;
  // Only areas switched on somewhere in the firm — plus anything this role already grants, so an old grant
  // to a since-disabled module stays visible (and removable) instead of silently lingering.
  const active = new Set(activeAreas || (catalog || []).map((a) => a.key));
  const shown = (catalog || []).filter((a) => active.has(a.key) || perms[a.key]);
  const hiddenCount = (catalog || []).length - shown.length;

  const save = async () => {
    if (!name.trim()) { toast.error("Give the role a name"); return; }
    setBusy(true);
    try {
      const payload = { name: name.trim(), description: description.trim(), permissions: perms };
      if (companyId) payload.company_id = companyId;
      if (role?.id) await api.put(`/company/roles/${role.id}`, payload);
      else await api.post(`/company/roles`, payload);
      toast.success(role?.id ? "Role updated" : "Role created");
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e, "Couldn't save the role")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="role-editor">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-redline-500" /> {role?.id ? `Edit ${role.name}` : "New role"}
          </DialogTitle>
          <DialogDescription className="sr-only">Name the role and choose what each part of the console it can open.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 max-h-[72vh] overflow-y-auto pr-1">
          {!role?.id && templates && (
            <div className="flex flex-wrap items-center gap-2" data-testid="role-templates">
              <span className="text-xs font-semibold text-ink/50">Start from:</span>
              {Object.entries(templates).map(([k, t]) => (
                <button key={k} type="button" data-testid={`role-template-${k}`} onClick={() => applyTemplate(k)}
                  className="inline-flex items-center gap-1 rounded-full border border-sand-200 px-3 py-1 text-xs font-semibold text-ink/70 hover:border-redline-400 hover:text-redline-600 transition-colors">
                  <Sparkles className="h-3 w-3" /> {t.name}
                </button>
              ))}
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold">Role name</label>
              <input data-testid="role-name" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Accountant" className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
            </div>
            <div>
              <label className="text-sm font-semibold">Description <span className="font-normal text-muted-foreground">(optional)</span></label>
              <input data-testid="role-description" value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder="What this person does" className="mt-1 w-full rounded-xl border border-sand-200 px-4 py-2.5 outline-none focus:ring-2 focus:ring-redline-500/30" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-semibold flex items-center gap-1.5">What they can open
                <InfoTip side="right">
                  <p className="font-semibold text-ink mb-1">Think of it as a {V.role("hoa_manager")} with most of the sidebar removed.</p>
                  <p><b>No access</b> — the page is hidden. <b>View</b> — they can open it and read everything, but every change is refused. <b>Can edit</b> — the same day-to-day actions a {V.role("hoa_manager")} has there.</p>
                  <p className="mt-1">Never included: settings, team invites, billing, add-ons — those need a real seat. Hover ⓘ on a row for exactly what each level does.</p>
                </InfoTip>
              </label>
              <span className="text-xs text-ink/50">{grantedCount} area{grantedCount === 1 ? "" : "s"} granted</span>
            </div>
            <div className="rounded-2xl border border-sand-200 divide-y divide-sand-100 overflow-hidden">
              {shown.map((a) => (
                <div key={a.key} className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-sand-50/60" data-testid={`role-area-${a.key}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-ink flex items-center gap-1.5">
                      {a.label}
                      {(a.view || a.edit) && (
                        <span data-testid={`role-area-${a.key}-info`}><InfoTip side="right">
                          <p><b>View:</b> {a.view}</p>
                          <p className="mt-1"><b>Can edit:</b> {a.edit}</p>
                        </InfoTip></span>
                      )}
                      {a.resident_data && <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 rounded-full px-1.5 py-px">resident data</span>}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{a.desc}</div>
                    {VIEW_ONLY_AREAS[a.key] && <div className="text-[11px] text-ink/60 mt-0.5" data-testid={`role-area-${a.key}-view-cap`}>{VIEW_ONLY_AREAS[a.key]}</div>}
                  </div>
                  <LevelPicker testid={`role-level-${a.key}`} value={perms[a.key] === "edit" && VIEW_ONLY_AREAS[a.key] ? "view" : perms[a.key]} viewCap={VIEW_ONLY_AREAS[a.key]} onChange={(lv) => setArea(a.key, lv)} />
                </div>
              ))}
            </div>
            {hiddenCount > 0 && <p className="text-[11px] text-ink/50 mt-1.5" data-testid="role-areas-hidden">{hiddenCount} inactive module{hiddenCount === 1 ? "" : "s"} hidden — modules none of your {V.lcs("org")} have switched on don't need a permission.</p>}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-sand-100">
          <button data-testid="role-cancel" onClick={onClose} className="rounded-full border border-sand-200 px-4 py-2 text-sm font-semibold hover:border-ink transition-colors">Cancel</button>
          <button data-testid="role-save" disabled={busy} onClick={save}
            className="rounded-full bg-redline-500 text-white font-semibold px-6 py-2 hover:bg-redline-600 transition-colors disabled:opacity-50">
            {busy ? "Saving…" : role?.id ? "Save changes" : "Create role"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Roles() {
  const { actualRole, viewCompanyId } = useDash();
  const companyId = actualRole === "company_admin" ? null : (viewCompanyId || null);
  const [data, setData] = useState({ roles: [], catalog: [], templates: {}, levels: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // role object or {} for new
  const cq = companyId ? `?company_id=${companyId}` : "";

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/company/roles${cq}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e, "Couldn't load roles"))).finally(() => setLoading(false));
  }, [cq]);
  useEffect(() => { load(); }, [load]);

  const remove = async (r) => {
    if (!window.confirm(`Delete the "${r.name}" role?`)) return;
    try { await api.delete(`/company/roles/${r.id}`); toast.success("Role deleted"); load(); }
    catch (e) { toast.error(errMsg(e, "Couldn't delete")); }
  };

  const labelFor = (k) => (data.catalog.find((a) => a.key === k) || {}).label || k;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="roles-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <PageTitle title={<><ShieldCheck className="h-6 w-6 text-redline-500" strokeWidth={1.75} /> Roles</>} about="Define your own console roles — like an Accountant who only sees reports and invoices — then assign them to people on the Team page. Pick what each role can open, and whether it's view-only or can edit." />
        </div>
        <button data-testid="role-new" onClick={() => setEditing({})}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-redline-500 text-white font-semibold px-4 py-2.5 hover:bg-redline-600 transition-colors">
          <Plus className="h-4 w-4" strokeWidth={2} /> New role
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-ink/40 py-10 text-center">Loading roles…</div>
      ) : data.roles.length === 0 ? (
        <div data-testid="roles-empty" className="rounded-2xl border border-dashed border-sand-300 bg-sand-50/50 p-10 text-center">
          <ShieldCheck className="h-8 w-8 text-ink/25 mx-auto mb-3" strokeWidth={1.5} />
          <div className="font-heading font-bold text-lg">No custom roles yet</div>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">Beyond {V.role("company_admin")} and {V.role("hoa_manager")}, you can create roles like <strong>Accountant</strong> or a read-only <strong>Auditor</strong> and control exactly what they see.</p>
          <button data-testid="roles-empty-new" onClick={() => setEditing({})}
            className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-redline-500 text-white font-semibold px-4 py-2.5 hover:bg-redline-600 transition-colors">
            <Plus className="h-4 w-4" strokeWidth={2} /> Create your first role
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {data.roles.map((r) => {
            const entries = Object.entries(r.permissions || {}).filter(([, v]) => v === "view" || v === "edit");
            return (
              <div key={r.id} data-testid={`role-card-${r.id}`} className="rounded-2xl border border-sand-200 bg-white p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-lg">{r.name}</div>
                    {r.description && <div className="text-sm text-muted-foreground">{r.description}</div>}
                    <div className="mt-1 inline-flex items-center gap-1 text-xs text-ink/50">
                      <Users2 className="h-3.5 w-3.5" /> {r.assigned_count} {r.assigned_count === 1 ? "person" : "people"} using this role
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`role-edit-${r.id}`} onClick={() => setEditing(r)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 text-ink/70 text-sm font-semibold px-3 py-1.5 hover:border-ink hover:text-ink transition-colors">
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </button>
                    <button data-testid={`role-delete-${r.id}`} onClick={() => remove(r)} title={r.assigned_count ? "Reassign people first" : "Delete role"}
                      className="inline-flex items-center gap-1 rounded-full border border-sand-200 text-ink/50 text-sm font-semibold px-2.5 py-1.5 hover:border-redline-300 hover:text-redline-600 transition-colors">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                {entries.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`role-perms-${r.id}`}>
                    {entries.map(([k, v]) => {
                      const M = LEVEL_META[v];
                      return (
                        <span key={k} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${v === "edit" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-sky-200 bg-sky-50 text-sky-800"}`}>
                          <M.icon className="h-3 w-3" /> {labelFor(k)}
                        </span>
                      );
                    })}
                  </div>
                ) : (
                  <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1">
                    <Ban className="h-3 w-3" /> No access granted yet — edit to give this role something to do.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <RoleEditor open={!!editing} onClose={() => setEditing(null)} role={editing.id ? editing : null}
          catalog={data.catalog} activeAreas={data.active_areas} templates={data.templates} companyId={companyId} onSaved={load} />
      )}
    </div>
  );
}
